// 创建 Fetch 客户端
export class FetchClient {
    constructor(config) {
        this.config = {
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
            ...config,
        };
    }
    setToken(token) {
        this.token = token;
    }
    clearToken() {
        this.token = undefined;
    }
    buildUrl(endpoint, params, query) {
        let url = `${this.config.baseUrl}${endpoint}`;
        // 替换路径参数
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                url = url.replace(`{${key}}`, encodeURIComponent(String(value)));
            });
        }
        // 添加查询参数
        if (query) {
            const searchParams = new URLSearchParams();
            Object.entries(query).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    searchParams.append(key, String(value));
                }
            });
            const queryString = searchParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }
        return url;
    }
    async request(method, endpoint, config = {}) {
        const { params, query, body, headers, ...requestInit } = config;
        const url = this.buildUrl(endpoint, params, query);
        const requestHeaders = {
            ...(this.config.headers || {}),
            ...(headers || {}),
        };
        if (this.token) {
            requestHeaders.Authorization = `Bearer ${this.token}`;
        }
        const requestConfig = {
            method,
            headers: requestHeaders,
            ...requestInit,
        };
        if (body) {
            requestConfig.body = JSON.stringify(body);
        }
        if (this.config.beforeRequest) {
            const modifiedConfig = await this.config.beforeRequest({ url, ...requestConfig });
            Object.assign(requestConfig, modifiedConfig);
        }
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
            const response = await fetch(url, {
                ...requestConfig,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            if (this.config.afterResponse) {
                return await this.config.afterResponse(response, data);
            }
            return data;
        }
        catch (error) {
            if (this.config.onError) {
                this.config.onError(error);
            }
            throw error;
        }
    }
    get(endpoint, config) {
        return this.request('GET', endpoint, config);
    }
    post(endpoint, config) {
        return this.request('POST', endpoint, config);
    }
    put(endpoint, config) {
        return this.request('PUT', endpoint, config);
    }
    delete(endpoint, config) {
        return this.request('DELETE', endpoint, config);
    }
    patch(endpoint, config) {
        return this.request('PATCH', endpoint, config);
    }
}
// 创建客户端实例的工厂函数
export function createFetchClient(baseUrl, options) {
    return new FetchClient({
        baseUrl,
        ...options,
    });
}
// 获取环境变量的函数（兼容浏览器和 Node.js 环境）
function getApiUrl() {
    var _a;
    // 在浏览器环境中，通常使用 window 对象或者打包工具注入的变量
    // 在 Node.js 环境中，使用 process.env
    if (typeof window !== 'undefined') {
        // 浏览器环境，尝试从全局变量获取
        return window.__API_URL__ || 'http://localhost:3000';
    }
    // Node.js 环境或构建时环境
    try {
        // 使用 globalThis 来访问全局对象
        const env = (_a = globalThis.process) === null || _a === void 0 ? void 0 : _a.env;
        return (env === null || env === void 0 ? void 0 : env.NEXT_PUBLIC_API_URL) || (env === null || env === void 0 ? void 0 : env.API_URL) || 'http://localhost:3000';
    }
    catch (_b) {
        return 'http://localhost:3000';
    }
}
// 默认客户端实例
export const apiClient = createFetchClient(getApiUrl());
// API 服务类
export class ApiService {
    constructor(client) {
        this.client = client;
    }
    // 认证相关
    async login(credentials) {
        return this.client.post('/api/auth/login', {
            body: credentials,
        });
    }
    async logout() {
        return this.client.post('/api/auth/logout');
    }
    async refreshToken(refreshToken) {
        return this.client.post('/api/auth/refresh', {
            body: { refreshToken },
        });
    }
    // 用户相关
    async getUsers(query) {
        return this.client.get('/api/users', { query });
    }
    async getUser(id) {
        return this.client.get('/api/users/{id}', {
            params: { id },
        });
    }
    async createUser(data) {
        return this.client.post('/api/users', {
            body: data,
        });
    }
    async updateUser(id, data) {
        return this.client.put('/api/users/{id}', {
            params: { id },
            body: data,
        });
    }
    async deleteUser(id) {
        return this.client.delete('/api/users/{id}', {
            params: { id },
        });
    }
    // 订单相关
    async getOrders(query) {
        return this.client.get('/api/orders', { query });
    }
    async getOrder(id) {
        return this.client.get('/api/orders/{id}', {
            params: { id },
        });
    }
    async createOrder(data) {
        return this.client.post('/api/orders', {
            body: data,
        });
    }
    async updateOrder(id, data) {
        return this.client.put('/api/orders/{id}', {
            params: { id },
            body: data,
        });
    }
    // 地址相关
    async getAddresses(query) {
        return this.client.get('/api/addresses', { query });
    }
    async getAddress(id) {
        return this.client.get('/api/addresses/{id}', {
            params: { id },
        });
    }
    async createAddress(data) {
        return this.client.post('/api/addresses', {
            body: data,
        });
    }
    async updateAddress(id, data) {
        return this.client.put('/api/addresses/{id}', {
            params: { id },
            body: data,
        });
    }
}
// 默认 API 服务实例
export const api = new ApiService(apiClient);
//# sourceMappingURL=client.js.map